import { useEffect, useState } from "react";
import api from "@/lib/api";
import { publicGet } from "@/lib/publicApi";
import MarketingShell from "@/components/MarketingShell";

/** /demos — public demo videos gallery. YouTube embeds are lazy-loaded
 *  via a click-to-play thumbnail so page speed is unaffected. */
export default function Demos() {
    const [videos, setVideos] = useState([]);
    const [playing, setPlaying] = useState(null);

    useEffect(() => {
        publicGet("/demo-videos").then(d => setVideos(d.items || []));
        document.title = "Demos | Doctorsprofile";
    }, []);

    return (
        <MarketingShell>
            <div className="max-w-6xl mx-auto py-14 px-6" data-testid="demos-page">
                <div className="text-xs uppercase tracking-wider text-[#17212B]/60 mb-2">Demos</div>
                <h1 className="text-4xl sm:text-5xl font-extrabold text-[#123B5D]">See the platform in action</h1>
                <p className="mt-3 text-lg text-[#17212B]/70 max-w-2xl">Product walkthroughs, onboarding demos and doctor success stories.</p>

                {videos.length === 0 && (
                    <div className="mt-10 text-[#17212B]/60">No demo videos have been added yet.</div>
                )}

                <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="demos-grid">
                    {videos.map(v => (
                        <div key={v.id} className="rounded-xl overflow-hidden border border-[#123B5D]/10 bg-white" data-testid={`demo-card-${v.id}`}>
                            {playing === v.id ? (
                                <div className="aspect-video bg-black">
                                    <iframe
                                        title={v.title}
                                        className="w-full h-full"
                                        src={`https://www.youtube.com/embed/${v.youtube_id}?autoplay=1&rel=0`}
                                        allow="autoplay; encrypted-media; picture-in-picture"
                                        allowFullScreen />
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => setPlaying(v.id)}
                                    className="relative w-full aspect-video block group"
                                    data-testid={`demo-play-${v.id}`}
                                    aria-label={`Play ${v.title}`}>
                                    <img
                                        src={`https://i.ytimg.com/vi/${v.youtube_id}/hqdefault.jpg`}
                                        alt={v.title}
                                        loading="lazy"
                                        className="w-full h-full object-cover" />
                                    <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 flex items-center justify-center transition-colors">
                                        <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                                            <svg viewBox="0 0 24 24" fill="#123B5D" className="w-6 h-6 ml-1">
                                                <path d="M8 5v14l11-7z" />
                                            </svg>
                                        </div>
                                    </div>
                                </button>
                            )}
                            <div className="p-4">
                                <div className="text-xs uppercase tracking-wider text-[#17212B]/60">{v.category}</div>
                                <h3 className="mt-1 font-bold text-[#17212B]">{v.title}</h3>
                                {v.description && (
                                    <p className="mt-1 text-sm text-[#17212B]/70">{v.description}</p>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </MarketingShell>
    );
}
